export interface FtColumn<T> {
  readonly key: string;
  readonly header: string;
  readonly cell: (row: T) => HTMLElement | string;
  readonly sortable?: boolean;
  readonly sortAccessor?: (row: T) => string | number;
  readonly cellClassName?: string;
}

export interface FilterDef<T> {
  readonly id: string;
  readonly label: string;
  readonly options: ReadonlyArray<{ value: string; label: string }>;
  readonly accessor: (row: T) => string;
  readonly defaultValue?: string; // 既定の選択値(未指定は "all")
}

export interface FilterableTableProps<T> {
  readonly caption: string;
  readonly columns: ReadonlyArray<FtColumn<T>>;
  readonly rows: ReadonlyArray<T>;
  readonly filters?: ReadonlyArray<FilterDef<T>>;
  readonly searchable?: boolean;
  readonly searchAccessor?: (row: T) => string;
  readonly onRowClick?: (row: T) => void;
  readonly emptyMessage?: string;
  readonly rowTestId?: string;
  // 絞り込み/検索/並べ替え後に実際に表示している行。CSV 出力などを「表示中の行」に合わせるために使う。
  // 初期描画時にも 1 度呼ばれる。
  readonly onVisibleRowsChange?: (rows: readonly T[]) => void;
}

export function createFilterableTable<T>(props: FilterableTableProps<T>): HTMLElement {
  const root = document.createElement("div");
  const filterValues = new Map<string, string>();
  for (const f of props.filters ?? []) filterValues.set(f.id, f.defaultValue ?? "all");
  let search = "";
  let sortKey: string | null = null;
  let sortDir: "asc" | "desc" = "asc";

  const bar = document.createElement("div");
  bar.className = "mb-4 flex flex-wrap items-end gap-3";
  for (const f of props.filters ?? []) {
    const wrap = document.createElement("div");
    const label = document.createElement("label");
    label.className = "block text-xs font-medium text-slate-600";
    label.htmlFor = "ft-" + f.id;
    label.textContent = f.label;
    const sel = document.createElement("select");
    sel.id = "ft-" + f.id;
    sel.className = "mt-1 rounded border border-slate-300 px-2 py-1 text-sm";
    const def = f.defaultValue ?? "all";
    for (const o of [{ value: "all", label: "すべて" }, ...f.options]) {
      const opt = document.createElement("option");
      opt.value = o.value;
      opt.textContent = o.label;
      if (o.value === def) opt.selected = true;
      sel.append(opt);
    }
    sel.addEventListener("change", () => {
      filterValues.set(f.id, sel.value);
      refresh();
    });
    wrap.append(label, sel);
    bar.append(wrap);
  }
  if (props.searchable === true) {
    const wrap = document.createElement("div");
    const label = document.createElement("label");
    label.className = "block text-xs font-medium text-slate-600";
    label.htmlFor = "ft-search";
    label.textContent = "検索";
    const input = document.createElement("input");
    input.id = "ft-search";
    input.type = "search";
    input.className = "mt-1 rounded border border-slate-300 px-2 py-1 text-sm";
    input.addEventListener("input", () => {
      search = input.value.trim();
      refresh();
    });
    wrap.append(label, input);
    bar.append(wrap);
  }

  const count = document.createElement("p");
  count.className = "mb-2 text-sm text-slate-500";
  const tableHost = document.createElement("div");

  function visibleRows(): T[] {
    let rows = [...props.rows];
    for (const f of props.filters ?? []) {
      const v = filterValues.get(f.id) ?? "all";
      if (v !== "all") rows = rows.filter((r) => f.accessor(r) === v);
    }
    if (props.searchable === true && search !== "") {
      const acc = props.searchAccessor;
      if (acc !== undefined) rows = rows.filter((r) => acc(r).includes(search));
    }
    if (sortKey !== null) {
      const col = props.columns.find((c) => c.key === sortKey);
      const acc = col?.sortAccessor;
      if (acc !== undefined) {
        rows.sort((a, b) => {
          const av = acc(a);
          const bv = acc(b);
          const cmp = typeof av === "number" && typeof bv === "number" ? av - bv : String(av).localeCompare(String(bv));
          return sortDir === "asc" ? cmp : -cmp;
        });
      }
    }
    return rows;
  }

  function toggleSort(key: string): void {
    if (sortKey === key) {
      sortDir = sortDir === "asc" ? "desc" : "asc";
    } else {
      sortKey = key;
      sortDir = "asc";
    }
    refresh();
  }

  function buildTable(rows: readonly T[]): HTMLElement {
    const table = document.createElement("table");
    table.className = "w-full border-collapse text-sm";
    const caption = document.createElement("caption");
    caption.className = "sr-only";
    caption.textContent = props.caption;
    const thead = document.createElement("thead");
    const headRow = document.createElement("tr");
    for (const col of props.columns) {
      const th = document.createElement("th");
      th.scope = "col";
      th.className = "border-b border-slate-200 py-2 px-2 text-left font-medium text-slate-700";
      if (col.sortable === true && col.sortAccessor !== undefined) {
        th.setAttribute("aria-sort", sortKey === col.key ? (sortDir === "asc" ? "ascending" : "descending") : "none");
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "inline-flex items-center gap-1 font-medium text-slate-700 hover:text-slate-900";
        const indicator = sortKey === col.key ? (sortDir === "asc" ? " ▲" : " ▼") : "";
        btn.textContent = col.header + indicator;
        btn.addEventListener("click", () => toggleSort(col.key));
        th.append(btn);
      } else {
        th.textContent = col.header;
      }
      headRow.append(th);
    }
    thead.append(headRow);

    const tbody = document.createElement("tbody");
    if (rows.length === 0) {
      const tr = document.createElement("tr");
      const td = document.createElement("td");
      td.colSpan = props.columns.length;
      td.className = "py-4 px-2 text-slate-500";
      td.textContent = props.emptyMessage ?? "データがありません。";
      tr.append(td);
      tbody.append(tr);
    } else {
      for (const row of rows) {
        const tr = document.createElement("tr");
        tr.className = "border-b border-slate-100";
        if (props.rowTestId !== undefined) tr.dataset["testid"] = props.rowTestId;
        if (props.onRowClick !== undefined) {
          tr.tabIndex = 0;
          tr.className += " cursor-pointer hover:bg-slate-50";
          const fire = (): void => props.onRowClick!(row);
          tr.addEventListener("click", fire);
          tr.addEventListener("keydown", (e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              fire();
            }
          });
        }
        for (const col of props.columns) {
          const td = document.createElement("td");
          td.className = "py-2 px-2 align-top text-slate-800 " + (col.cellClassName ?? "");
          const content = col.cell(row);
          if (typeof content === "string") td.textContent = content;
          else td.append(content);
          tr.append(td);
        }
        tbody.append(tr);
      }
    }
    table.append(caption, thead, tbody);
    return table;
  }

  function refresh(): void {
    const rows = visibleRows();
    count.textContent = rows.length + " 件";
    tableHost.replaceChildren(buildTable(rows));
    props.onVisibleRowsChange?.(rows);
  }

  refresh();
  root.append(bar, count, tableHost);
  return root;
}
